import React, { useState, useRef, useMemo } from 'react';
import { Download, Check, Loader2 } from 'lucide-react';
import type { FileCreationBlock } from '@/types/contentBlocks';
import { exportHTMLToPDF, exportToCSV, exportToText } from '@/utils/fileExporter';
import DOMPurify from 'dompurify';

interface FileCreationBlockProps {
  block: FileCreationBlock;
  isDarkMode: boolean;
}

export const FileCreationBlockRenderer: React.FC<FileCreationBlockProps> = ({ block, isDarkMode }) => {
  const { fileType, filename, content } = block;
  const [downloadState, setDownloadState] = useState<'idle' | 'generating' | 'success'>('idle');
  
  const printAreaRef = useRef<HTMLDivElement>(null);

  // Estimate file size based on content length
  const estimateSize = () => {
    const bytes = new Blob([content]).size;
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  // Convert markdown-style content to styled HTML for PDF preview
  const renderedHtmlContent = useMemo(() => {
    if (fileType !== 'pdf' && fileType !== 'text') return '';

    const purifyConfig = {
      ADD_TAGS: [
        'svg', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 
        'text', 'tspan', 'g', 'defs', 'linearGradient', 'stop', 'style', 'img', 'table',
        'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span', 'br', 'hr', 'p', 'h1', 'h2', 'h3',
        'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'strong', 'em', 'code', 'pre'
      ],
      ADD_ATTR: [
        'd', 'viewBox', 'fill', 'stroke', 'stroke-width', 'x', 'y', 'width', 'height', 
        'rx', 'ry', 'x1', 'y1', 'x2', 'y2', 'offset', 'stop-color', 'stop-opacity', 
        'points', 'transform', 'style', 'class', 'id', 'src', 'alt', 'width', 'height', 
        'border', 'cellpadding', 'cellspacing', 'align', 'valign'
      ]
    };

    // Check if the content is already HTML
    const isHtml = /<[a-z][\s\S]*>/i.test(content);
    if (isHtml) {
      return DOMPurify.sanitize(content, purifyConfig);
    }

    // Otherwise, parse markdown and wrap it with premium inline CSS
    const rawLines = content.split('\n');
    let htmlResult = '';
    let inList = false;
    let inTable = false;
    let tableHeaders: string[] = [];
    let tableRows: string[][] = [];

    rawLines.forEach(line => {
      const trimmed = line.trim();

      // Handle table completion
      if (inTable && (!trimmed.startsWith('|') || trimmed === '')) {
        inTable = false;
        htmlResult += `
          <div style="margin: 20px 0; overflow-x: auto; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);">
            <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 14px;">
              <thead>
                <tr style="background: linear-gradient(135deg, #f8fafc, #f1f5f9); border-bottom: 2px solid #e2e8f0;">
                  ${tableHeaders.map(h => `<th style="padding: 12px 16px; font-weight: 600; color: #1e293b; border-right: 1px solid #e2e8f0;">${h}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${tableRows.map((row, ridx) => `
                  <tr style="border-bottom: 1px solid #e2e8f0; background: ${ridx % 2 === 0 ? '#ffffff' : '#f8fafc'}; transition: background 0.15s;">
                    ${row.map(cell => `<td style="padding: 12px 16px; color: #334155; border-right: 1px solid #e2e8f0;">${cell}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `;
        tableHeaders = [];
        tableRows = [];
      }

      // Handle list completion
      if (inList && !trimmed.startsWith('-') && !trimmed.startsWith('*') && !/^\d+\./.test(trimmed)) {
        inList = false;
        htmlResult += '</ul>';
      }

      if (trimmed === '') {
        htmlResult += '<div style="height: 12px;"></div>';
        return;
      }

      // Headings
      if (trimmed.startsWith('# ')) {
        htmlResult += `<h1 style="font-size: 26px; color: #4c1d95; font-weight: 800; border-bottom: 2px solid #ddd6fe; padding-bottom: 6px; margin-top: 28px; margin-bottom: 14px; font-family: 'Outfit', sans-serif;">${trimmed.substring(2)}</h1>`;
      } else if (trimmed.startsWith('## ')) {
        htmlResult += `<h2 style="font-size: 20px; color: #6d28d9; font-weight: 700; margin-top: 22px; margin-bottom: 10px; font-family: 'Outfit', sans-serif;">${trimmed.substring(3)}</h2>`;
      } else if (trimmed.startsWith('### ')) {
        htmlResult += `<h3 style="font-size: 16px; color: #1e1b4b; font-weight: 600; margin-top: 18px; margin-bottom: 8px; font-family: 'Outfit', sans-serif;">${trimmed.substring(4)}</h3>`;
      }
      // Bullet lists
      else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        if (!inList) {
          inList = true;
          htmlResult += '<ul style="margin: 12px 0 12px 6px; padding-left: 20px;">';
        }
        htmlResult += `<li style="margin-bottom: 6px; color: #334155; font-size: 15px; line-height: 1.6;">${trimmed.substring(2)}</li>`;
      }
      // Tables
      else if (trimmed.startsWith('|')) {
        const parts = trimmed.split('|').map(p => p.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
        // Skip separator lines like |---|---|
        if (parts.every(p => p.startsWith('-') || p.startsWith(':'))) {
          return;
        }
        if (!inTable) {
          inTable = true;
          tableHeaders = parts;
        } else {
          tableRows.push(parts);
        }
      }
      // Horizontal Rule
      else if (trimmed === '---' || trimmed === '***') {
        htmlResult += '<hr style="border: none; border-top: 1px solid #ddd6fe; margin: 24px 0;" />';
      }
      // Paragraph
      else {
        // Parse bold and italics minimally
        let formattedText = trimmed
          .replace(/\*\*([^*]+)\*\*/g, '<strong style="color:#4c1d95;font-weight:600;">$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em style="color:#6d28d9;">$1</em>')
          .replace(/`([^`]+)`/g, '<code style="background-color:#f5f3ff;color:#6d28d9;padding:2px 6px;border-radius:4px;font-size:14px;font-family:monospace;">$1</code>');
        
        htmlResult += `<p style="font-size: 15px; line-height: 1.65; color: #334155; margin-bottom: 12px;">${formattedText}</p>`;
      }
    });

    // Cleanup active structures
    if (inTable) {
      htmlResult += `
        <div style="margin: 20px 0; overflow-x: auto; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 14px;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f8fafc, #f1f5f9); border-bottom: 2px solid #e2e8f0;">
                ${tableHeaders.map(h => `<th style="padding: 12px 16px; font-weight: 600; color: #1e293b; border-right: 1px solid #e2e8f0;">${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableRows.map((row, ridx) => `
                <tr style="border-bottom: 1px solid #e2e8f0; background: ${ridx % 2 === 0 ? '#ffffff' : '#f8fafc'}; transition: background 0.15s;">
                  ${row.map(cell => `<td style="padding: 12px 16px; color: #334155; border-right: 1px solid #e2e8f0;">${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }
    if (inList) {
      htmlResult += '</ul>';
    }

    return DOMPurify.sanitize(htmlResult, purifyConfig);
  }, [content, fileType]);

  const handleDownload = async () => {
    if (downloadState !== 'idle') return;

    setDownloadState('generating');

    // Simulate PDF compile delay for a premium feel
    setTimeout(async () => {
      try {
        if (fileType === 'pdf') {
          if (printAreaRef.current) {
            await exportHTMLToPDF(printAreaRef.current, filename);
          } else {
            await exportToText(filename, content);
          }
        } else if (fileType === 'excel' || fileType === 'csv') {
          exportToCSV(filename, content);
        } else {
          exportToText(filename, content);
        }
        setDownloadState('success');
        
        setTimeout(() => {
          setDownloadState('idle');
        }, 2000);
      } catch (error) {
        console.error('File export failed:', error);
        setDownloadState('idle');
      }
    }, 900);
  };

  const fileExtension = filename.includes('.') ? filename.split('.').pop() || fileType : fileType;

  const getThemeColors = () => {
    switch (fileType) {
      case 'excel':
      case 'csv':
        return {
          tagBg: isDarkMode ? 'bg-green-900/30' : 'bg-green-100',
          tagText: isDarkMode ? 'text-green-400' : 'text-green-700',
          tagBorder: isDarkMode ? 'border-green-800/50' : 'border-green-200',
        };
      case 'pdf':
        return {
          tagBg: isDarkMode ? 'bg-red-900/30' : 'bg-red-100',
          tagText: isDarkMode ? 'text-red-400' : 'text-red-700',
          tagBorder: isDarkMode ? 'border-red-800/50' : 'border-red-200',
        };
      case 'text':
      default:
        return {
          tagBg: isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100',
          tagText: isDarkMode ? 'text-blue-400' : 'text-blue-700',
          tagBorder: isDarkMode ? 'border-blue-800/50' : 'border-blue-200',
        };
    }
  };

  const theme = getThemeColors();

  const renderIllustration = () => {
    switch (fileType) {
      case 'excel':
      case 'csv':
        return (
          <div className="absolute right-[-10px] bottom-[-10px] opacity-[0.25] group-hover/thumbnail:opacity-[0.4] group-hover/thumbnail:scale-110 transition-all duration-300 pointer-events-none">
            <svg width="68" height="68" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
              <polyline points="14 2 14 8 20 8" />
              <path d="M2 15h10" />
              <path d="M2 18h10" />
              <path d="M2 12h10" />
              <rect x="2" y="12" width="10" height="9" rx="1" />
            </svg>
          </div>
        );
      case 'pdf':
        return (
          <div className="absolute right-[-10px] bottom-[-10px] opacity-[0.25] group-hover/thumbnail:opacity-[0.4] group-hover/thumbnail:scale-110 transition-all duration-300 pointer-events-none">
            <svg width="68" height="68" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <path d="M10 9H8" />
              <path d="M16 13H8" />
              <path d="M16 17H8" />
            </svg>
          </div>
        );
      case 'text':
      default:
        return (
          <div className="absolute right-[-10px] bottom-[-10px] opacity-[0.25] group-hover/thumbnail:opacity-[0.4] group-hover/thumbnail:scale-110 transition-all duration-300 pointer-events-none">
            <svg width="68" height="68" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="my-4 w-full flex flex-col items-start gap-4">
      {/* Compact container matching chat attachments layout */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Preview box exactly same as we show in the chatinput attached files preview box */}
        <div className="group/thumbnail relative pb-[10px]" data-testid="file-thumbnail" onClick={handleDownload} style={{ cursor: downloadState === 'idle' ? 'pointer' : 'default' }}>
          
          <div
            className={`rounded-lg text-left block transition-all border flex flex-col justify-between gap-2.5 overflow-hidden px-2.5 py-2 shadow-sm w-[120px] h-[120px] min-w-[120px] relative z-10 ${
              isDarkMode ? 'bg-[#1f1f1f] border-[#3a3a3a]' : 'bg-white border-gray-200'
            } ${isDarkMode ? 'hover:border-[#5a5a5a]' : 'hover:border-gray-300'} hover:shadow-md`}
          >
            {renderIllustration()}

            <div className="flex flex-col gap-1 min-h-0 relative z-20">
              <h3 className={`text-[12px] break-words line-clamp-3 font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                {filename}
              </h3>
              <p className={`text-[10px] line-clamp-1 break-words ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {estimateSize()}
              </p>
            </div>
            <div className="relative z-20">
              <div className="relative flex flex-row items-center gap-1 justify-between">
                <div className="flex flex-row gap-1 shrink min-w-0">
                  <div className={`min-w-0 h-[18px] flex flex-row items-center justify-center gap-0.5 px-1.5 border shadow-sm rounded font-bold backdrop-blur-sm ${
                    theme.tagBorder
                  } ${theme.tagBg} ${theme.tagText}`}>
                    <p className="uppercase truncate text-[9px] leading-[13px] tracking-wider font-semibold">
                      {fileExtension}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {downloadState === 'generating' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-30">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
            {downloadState === 'success' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-30">
                <Check className="w-6 h-6 text-green-400" />
              </div>
            )}
          </div>
          
        </div>
      </div>

      {/* Hidden print element off-screen for html2canvas to capture */}
      {fileType === 'pdf' && (
        <div
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '0',
            width: '794px', // A4 Standard Width
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
          }}
        >
          <div
            ref={printAreaRef}
            className="w-full p-12 text-black text-left select-text relative flex flex-col justify-between"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            <div
              className="document-html-content"
              dangerouslySetInnerHTML={{ __html: renderedHtmlContent }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FileCreationBlockRenderer;
